/**
 * Applicant submission eligibility — pure helpers + dynamic copy matrix.
 *
 * See `docs/CLAUDE-applicant-submission-eligibility.md` for the full spec
 * (tier table, 10-cell matrix, post-submission copy, banned-words rules).
 *
 * Counts are derived from existing `SessionStatus` + `VoucherInvitation[]`.
 * No new backend fields are required to ship the FE rewire. When backend
 * §10.6 lands (`vouch_summary` on SessionStatus), swap the invitation-list
 * inputs for those server values without touching this file's public API.
 */

import type { SessionStatus, VoucherInvitation } from "@/lib/api";

/**
 * Connected accounts that count toward the invite-requirement tier.
 * Email OTP and Plaid remain supporting evidence only.
 */
const NON_OAUTH_PROVIDERS = new Set(["email", "plaid"]);

/**
 * Canonical OAuth provider ids that count toward the account-check tier.
 *
 * ⚠️ `"instagram"` is deliberately ABSENT, and stays absent. It was never a
 * provider we run — it was the key `ApplicantFlow` sent to the manual-submit
 * path for the Facebook tile, which wrote an operator-review row and nothing
 * else. The platform counts this tier from `oauth_states` (completed
 * server-side OAuth flows only), so a manual submit never counted there and
 * must not count here. Platform #442 retired that path, so no id can arrive
 * by it any more; the exclusion is kept because Instagram has no OAuth route
 * either (platform #522, #523), and adding the id would silently grant tier
 * credit for something no applicant can complete.
 *
 * `"facebook"` STAYS. Facebook is a real OAuth provider as of platform #424 and
 * is named in the platform's own tier set, so a genuine Facebook connection
 * — one that arrives as a `connected_providers` entry — earns the credit like
 * any other. What is excluded is the manual-submit path, not the provider.
 */
const OAUTH_PROVIDER_IDS = new Set([
  "google",
  "microsoft",
  "outlook",
  "hotmail",
  "live",
  "msn",
  "linkedin",
  "facebook",
  "amazon",
  "apple",
  "uber",
  // A real sign-in that proves control of the account, which is what the
  // server's tier counts. Not an email proof: that is decided separately, in
  // `@/lib/emailProof`.
  "github",
]);

/**
 * When backend stores an OAuth email-step connection as `provider: "email"`,
 * the masked identifier is the only signal of which underlying OAuth provider
 * was used. Map the domain back to the canonical provider id so a
 * Google-via-email-step OAuth still counts as an account check.
 */
const EMAIL_DOMAIN_TO_PROVIDER: Array<{ pattern: RegExp; provider: string }> = [
  { pattern: /@(?:gmail|googlemail)\.com$/i, provider: "google" },
  { pattern: /@(?:outlook|hotmail|live|msn)\.[a-z.]+$/i, provider: "microsoft" },
];

/**
 * The single place provider-id aliasing is decided.
 *
 * ⚠️ This used to fold `"instagram"` into `"facebook"`, which is exactly how a
 * manual Facebook submit earned tier credit: `ApplicantFlow` passes the
 * `liveProvider` key `"instagram"`, this turned it into `"facebook"`, and
 * `OAUTH_PROVIDER_IDS` accepted that. The alias is gone — do not restore it.
 * Nothing double-counts as a result, because `"instagram"` is no longer a
 * counting id at all: the two ids can never both be present.
 *
 * Kept as a function rather than inlined so the alias question has one obvious
 * home if a provider ever genuinely needs one.
 */
function canonicalizeProviderId(id: string): string {
  return id.toLowerCase();
}

/**
 * Fallback: recover a provider from a verified signal when the connection
 * itself has not surfaced in `connected_providers`.
 *
 * ⚠️ NO facebook/instagram matcher. A `digital.social_account` signal reading
 * "Facebook account verified" is what `reviewSocialProfile` writes after an
 * OPERATOR reviews a manual submission — it is evidence of a reviewed handle,
 * not of a completed OAuth flow, and the platform's tier count would not see
 * it. LinkedIn keeps its matcher despite claiming the same signal type,
 * because LinkedIn only ever reaches us through a real OAuth connection.
 */
const SIGNAL_PROVIDER_MATCHERS: Array<{ pattern: RegExp; provider: string }> = [
  { pattern: /amazon/i, provider: "amazon" },
  { pattern: /google|gmail/i, provider: "google" },
  { pattern: /linkedin/i, provider: "linkedin" },
  { pattern: /microsoft|outlook|hotmail|live|msn/i, provider: "microsoft" },
  { pattern: /uber/i, provider: "uber" },
  // No GitHub matcher, on purpose. These run unanchored over key, label AND
  // detail, and a GitHub detail line carries the account's handle, which can
  // contain another provider's name ("@oliver" matches live): one connection
  // would count as two. A GitHub connection counts through
  // `connected_providers`, which is where the server reports it.
];

/**
 * Vouch-invitation statuses that count as a sent, still-valid invite.
 *
 * `"complete"` is the applicant-facing status the backend emits for a voucher
 * who responded (see `VoucherInvitationStatus` in `@/lib/api` for the full
 * normalization table). `"accepted"` is retained only as a legacy alias — the
 * applicant endpoint has not emitted it since platform 36cab07 (2026-05-20).
 */
const INVITE_OPEN_STATUSES = new Set(["pending", "complete", "accepted"]);

/**
 * Vouch-invitation statuses that count as a completed vouch.
 *
 * ⚠️ `"complete"` means "the voucher responded" — it does NOT mean "the voucher
 * said yes". The backend collapses both `accepted` and `declined` into it, so
 * this count cannot distinguish a confirmed vouch from a refused one and must
 * never be treated as proof of a positive vouch. It drives applicant-facing
 * pre-flight copy only; the real gate is `reevaluateReadyForReview` on the
 * platform, which reads the un-normalized `outcome` column.
 *
 * 👤 Product follow-up (Juan + Patrick): under this normalization an applicant
 * whose only voucher DECLINED sees the same UI as one whose voucher confirmed,
 * so they are never prompted to invite a replacement and the inquiry sits in
 * `stepup_in_progress` indefinitely. Fixing that needs a product decision about
 * what the applicant is told, not just a counting change.
 */
const INVITE_COMPLETED_STATUSES = new Set(["complete", "accepted"]);

/** Session statuses that mean the applicant has submitted their application. */
const SUBMITTED_SESSION_STATUSES = new Set<SessionStatus["status"]>([
  "completed",
]);

// ---------- Counts ----------

export interface ConnectedOAuthExtra {
  /**
   * Provider ids the FE has just sent to the backend but which have not yet
   * hydrated into `session.connected_providers`. Lets the account-check count
   * update immediately instead of lagging the row's "connected" UI state.
   *
   * ⚠️ **No caller passes this today, and that is not an oversight.** Its only
   * caller fed it manual-submit keys, and platform #442 retired that path. An
   * OAuth connection takes the applicant off the page entirely and returns them
   * to a refreshed session, so there is no in-flight window left to paper over.
   *
   * Kept because the hook is route-agnostic and the filtering below is the part
   * that matters: every id is checked against `OAUTH_PROVIDER_IDS`, which is
   * **load-bearing rather than defensive** — it is what stopped manual keys
   * earning tier credit, and it is what would stop the next non-OAuth route
   * doing the same.
   */
  pendingProviderIds?: Iterable<string>;
}

/**
 * How many connected accounts count toward the vouch tier: canonical OAuth
 * providers, each counted once however many routes report it.
 *
 * ⚠️ THE INVARIANT: this must return what the platform's `loadEligibility`
 * returns. The platform enforces `canSubmit`; this only decides what the
 * applicant is TOLD. When this is more generous than the platform, the
 * applicant is told they need fewer vouches than the platform will accept,
 * sends exactly that many, and Submit fails with no action left to take — a
 * dead end, and one we manufactured.
 *
 * That was live until this change. The platform counts `oauth_states` rows
 * (completed server-side OAuth only) and so never counted a manual Facebook
 * submit; three separate paths here did — the `instagram` → `facebook` alias,
 * the `/facebook|instagram/` signal matcher, and `pendingProviderIds`. All
 * three are closed above, each at its own site, and the reasons are recorded
 * there because they are three different mistakes rather than one.
 *
 * Reachable on the `high_volume` lane specifically: `ApplicantFlow` hides the
 * Facebook tile only where the digital-account step is REQUIRED, so the lane
 * where it is optional is the lane where the tile shows and the trap opened.
 */
export function countConnectedOAuth(
  session: SessionStatus | null,
  extra?: ConnectedOAuthExtra,
): number {
  const connected = new Set<string>();

  if (session) {
    for (const provider of session.connected_providers ?? []) {
      if (provider.status !== "connected") continue;
      const id = provider.provider.toLowerCase();

      // OAuth-via-email-step: backend stores the connection as
      // `provider: "email"`. Map the masked identifier domain back to the
      // underlying OAuth provider so Google/Microsoft still count.
      if (id === "email") {
        const masked = provider.masked_identifier ?? "";
        for (const { pattern, provider: mapped } of EMAIL_DOMAIN_TO_PROVIDER) {
          if (pattern.test(masked)) {
            connected.add(canonicalizeProviderId(mapped));
            break;
          }
        }
        continue;
      }

      if (NON_OAUTH_PROVIDERS.has(id)) continue;
      if (!OAUTH_PROVIDER_IDS.has(id)) continue;
      connected.add(canonicalizeProviderId(id));
    }

    for (const signal of session.signals_summary ?? []) {
      if (signal.status !== "verified") continue;
      // A developer-account signal is GitHub's, and its text can carry the
      // account's handle ("@oliver" matches live). It counts through its
      // connection, never through text matching.
      if (/developer[_.]account/i.test(signal.key)) continue;
      const haystack = `${signal.key} ${signal.label} ${signal.detail ?? ""}`;
      for (const { pattern, provider } of SIGNAL_PROVIDER_MATCHERS) {
        if (pattern.test(haystack)) connected.add(canonicalizeProviderId(provider));
      }
    }
  }

  if (extra?.pendingProviderIds) {
    for (const raw of extra.pendingProviderIds) {
      if (!raw) continue;
      const id = canonicalizeProviderId(raw);
      if (NON_OAUTH_PROVIDERS.has(id)) continue;
      if (!OAUTH_PROVIDER_IDS.has(id)) continue;
      connected.add(id);
    }
  }

  return connected.size;
}

/**
 * True when an invitation is still "on the board" for the applicant — sent and
 * not withdrawn or lapsed, whether or not the voucher has responded yet.
 *
 * Exported so the UI list, the demo session's rollup and the counter below all
 * read the SAME set. Four separate hand-rolled copies of this predicate are how
 * `"complete"` came to be missing from some of them but not others.
 */
export function isOpenVouchInvite(status: VoucherInvitation["status"]): boolean {
  return INVITE_OPEN_STATUSES.has(status);
}

export function countSentVouchInvites(
  invitations: VoucherInvitation[] | null | undefined,
  session?: SessionStatus | null,
): number {
  if (invitations && invitations.length) {
    return invitations.filter((i) => INVITE_OPEN_STATUSES.has(i.status)).length;
  }
  // Fallback when invitation list is not yet loaded — the session payload
  // exposes a server-side rollup of in-flight vouches.
  return session?.pending_vouchers_count ?? 0;
}

export function countCompletedVouches(
  invitations: VoucherInvitation[] | null | undefined,
): number {
  if (!invitations) return 0;
  return invitations.filter((i) => INVITE_COMPLETED_STATUSES.has(i.status))
    .length;
}

/** 3+ → 1, 2 → 2, 1 → 3, 0 → null (cannot submit). */
export function requiredVouchesFor(oauth: number): number | null {
  if (oauth >= 3) return 1;
  if (oauth === 2) return 2;
  if (oauth === 1) return 3;
  return null;
}

/**
 * ADR-012 R2, Patrick's amendment — we always need at least one vouch.
 *
 * Changes no output today (the tier table already caps at 1). It exists so a
 * future edit to that table, or a larger credit, cannot quietly reduce the
 * requirement to zero and turn the flow into "verify your ID, skip the humans".
 *
 * MIRRORS `MIN_REQUIRED_VOUCHES` in digavouch-platform
 * services/digavouch/src/services/submission-eligibility.ts.
 */
export const MIN_REQUIRED_VOUCHES = 1;

/**
 * Required vouches after the ADR-012 R2 IDV credit.
 *
 * A verified applicant IDV counts as ONE additional verification for the TIER
 * only, in orgs that ask for applicant IDV:
 *
 *   2 OAuth + completed IDV → 1 vouch   (instead of 2)
 *   1 OAuth + completed IDV → 2 vouches (instead of 3)
 *   0 OAuth + completed IDV → still null — IDV never unlocks submission alone
 *
 * The `oauth >= 1` guard is on the CREDIT, not just on canSubmit. Without it,
 * 0 OAuth plus a verified IDV credits to 1 and turns null ("cannot submit")
 * into a 3-vouch tier — letting someone pay for a KYC check and skip everything
 * else, which R2 explicitly rejects. IDV says nothing about the digital
 * footprint the vouch tier exists to corroborate.
 *
 * Declining or failing simply doesn't earn the credit; it is never a penalty
 * (R3.1). Opting out costs exactly one more vouch and nothing else.
 *
 * ⚠️ MIRRORS the backend `computeEligibility` in digavouch-platform
 * services/digavouch/src/services/submission-eligibility.ts. The backend copy
 * is the real gate; this one drives the pre-flight UI. They live in different
 * repos and cannot ship in one PR, so if you change one, change the other and
 * diff them by hand. See digavouch-platform
 * docs/handoff/BRADEN_FELLMAN_ONBOARDING.md §9.
 */
export function requiredVouchesWithIdv(
  oauth: number,
  idvCreditEarned: boolean,
): number | null {
  const credit = idvCreditEarned && oauth >= 1 ? 1 : 0;
  if (oauth < 1) return null;
  const required = requiredVouchesFor(oauth + credit);
  // null means "no tier at all" rather than "zero" — a floor must not promote
  // a cannot-submit case into a submittable one.
  if (required === null) return null;
  return Math.max(MIN_REQUIRED_VOUCHES, required);
}

/**
 * Has this applicant earned the R2 credit?
 *
 * #525 — reads the backend's own computed answer (`idv_credit_earned` on
 * `GET /sessions/:token/status`) instead of deriving one from
 * `applicant_idv_required && applicant_idv_verified`. That derivation is
 * predicate (2) from the issue: `applicant_idv_verified` comes from a
 * cross-DB PortFi attestation lookup that can under-report relative to the
 * backend's real gate (`idv_captures.passed`), which is what
 * `idv_credit_earned` is sourced from. Reading the published field instead
 * of re-deriving it is what makes the pre-flight and the gate structurally
 * incapable of disagreeing — the whole point of the fix.
 *
 * Org-gating is still implicit: the backend only ever sets
 * `idv_credit_earned` true when `applicant_idv_required` is, so every
 * existing tenant stays on exactly today's numbers until BF-B3 flips the
 * flag. Absent (older backend, mid-rollout) reads as false.
 */
export function hasEarnedIdvCredit(session: SessionStatus | null): boolean {
  return session?.idv_credit_earned === true;
}

/**
 * Canonical helper for callers that always need a number. Mirrors the tier
 * table; 0 OAuth → 3 (the highest tier) so applicants without any account
 * checks still see the maximum invite requirement in the UI.
 */
export function getRequiredVouches(oauth: number): number {
  return requiredVouchesFor(oauth) ?? 3;
}

/**
 * The server's own vouch numbers, when this backend publishes them (verified
 * as of 2026-09-29, platform PR #718). The status payload carries, on EVERY
 * session, what the submit gate counts and decides: `evidence_pieces` (connected
 * accounts plus verified email addresses other than the application email),
 * `required` (the gate's number; null with 0 pieces, which cannot submit yet),
 * `owed` (what the screen shows: the gate's number, or with 0 pieces what the
 * first piece would ask for; on a Flow, the Flow's own number) and `can_submit`.
 *
 * Reading these instead of re-deriving the tier keeps the screen and the gate
 * from disagreeing. The browser cannot see which email addresses count, so it
 * cannot compute `evidence_pieces` at all.
 *
 * Null on an older backend, which keeps the tier computed here.
 */
export function serverVouchNumbers(
  session: Pick<SessionStatus, "vouch_summary" | "can_submit"> | null | undefined,
): {
  required: number | null;
  owed: number;
  canSubmit: boolean;
  evidencePieces: number;
} | null {
  const summary = session?.vouch_summary;
  if (!summary) return null;
  if (typeof summary.owed !== "number" || typeof summary.evidence_pieces !== "number") return null;
  if (typeof session?.can_submit !== "boolean") return null;
  return {
    required: typeof summary.required === "number" ? summary.required : null,
    owed: summary.owed,
    canSubmit: session.can_submit,
    evidencePieces: summary.evidence_pieces,
  };
}

export function isApplicantSubmitted(session: SessionStatus | null): boolean {
  if (!session) return false;
  if (session.submitted_at) return true;
  return SUBMITTED_SESSION_STATUSES.has(session.status);
}

// ---------- State + copy matrix ----------

export type SubmissionBucket =
  | "oauth_0"
  | "oauth_1_invites_0"
  | "oauth_1_invites_1"
  | "oauth_1_invites_2"
  | "oauth_1_ready"
  | "oauth_2_invites_0"
  | "oauth_2_invites_1"
  | "oauth_2_ready"
  | "oauth_3_invites_0"
  | "oauth_3_ready"
  | "submitted_awaiting_vouch"
  | "submitted_ready_for_review";

export interface SubmissionCopy {
  primary: string;
  secondary?: string;
  buttonLabel: string;
  buttonDisabled: boolean;
}

export interface SubmissionState {
  connectedOAuth: number;
  sentVouchInvites: number;
  completedVouches: number;
  requiredVouches: number | null;
  applicantSubmitted: boolean;
  canSubmit: boolean;
  readyForReview: boolean;
  bucket: SubmissionBucket;
  copy: SubmissionCopy;
}

const DISABLED_LABEL = "More verification needed";
const SUBMIT_LABEL = "Submit";

/** Rejection message used by both the FE pre-flight and the server (§5). */
export const SUBMISSION_REJECTED_MESSAGE =
  "Submission not ready. Please connect the required account and send the required vouch requests before submitting.";

const COPY: Record<SubmissionBucket, SubmissionCopy> = {
  oauth_0: {
    primary:
      "Connect an account, or verify an email address other than your application email, to continue. After that, we'll show how many vouch requests are needed.",
    buttonLabel: DISABLED_LABEL,
    buttonDisabled: true,
  },
  oauth_1_invites_0: {
    primary:
      "Send 3 vouch requests or connect 2 more accounts to submit.",
    buttonLabel: DISABLED_LABEL,
    buttonDisabled: true,
  },
  oauth_1_invites_1: {
    primary:
      "Send 2 more vouch requests or connect another account to reduce the vouch requirement.",
    buttonLabel: DISABLED_LABEL,
    buttonDisabled: true,
  },
  oauth_1_invites_2: {
    primary:
      "Send 1 more vouch request or connect another account to submit.",
    buttonLabel: DISABLED_LABEL,
    buttonDisabled: true,
  },
  oauth_1_ready: {
    primary:
      "You can submit now. Your application will move to review once your vouches are completed.",
    buttonLabel: SUBMIT_LABEL,
    buttonDisabled: false,
  },
  oauth_2_invites_0: {
    primary:
      "Send 2 vouch requests or connect 1 more account to reduce the vouch requirement.",
    buttonLabel: DISABLED_LABEL,
    buttonDisabled: true,
  },
  oauth_2_invites_1: {
    primary:
      "Send 1 more vouch request or connect 1 more account to submit.",
    buttonLabel: DISABLED_LABEL,
    buttonDisabled: true,
  },
  oauth_2_ready: {
    primary:
      "You can submit now. Your application will move to review once your vouches are completed.",
    buttonLabel: SUBMIT_LABEL,
    buttonDisabled: false,
  },
  oauth_3_invites_0: {
    primary: "Send 1 vouch request to submit.",
    buttonLabel: DISABLED_LABEL,
    buttonDisabled: true,
  },
  oauth_3_ready: {
    primary:
      "You can submit now. Your application will move to review once your vouch is completed.",
    buttonLabel: SUBMIT_LABEL,
    buttonDisabled: false,
  },
  submitted_awaiting_vouch: {
    primary:
      "Your submission has been sent. Your application will move to review once your vouches are completed.",
    secondary:
      "We'll update this automatically as your vouches come in.",
    buttonLabel: SUBMIT_LABEL,
    buttonDisabled: true,
  },
  submitted_ready_for_review: {
    primary:
      "Your submission has been sent. Your application will move to review once your vouches are completed.",
    secondary:
      "We'll update this automatically as your vouches come in.",
    buttonLabel: SUBMIT_LABEL,
    buttonDisabled: true,
  },
};

function resolveBucket(args: {
  oauth: number;
  invites: number;
  submitted: boolean;
  readyForReview: boolean;
  canSubmit: boolean;
}): SubmissionBucket {
  if (args.submitted) {
    return args.readyForReview
      ? "submitted_ready_for_review"
      : "submitted_awaiting_vouch";
  }

  if (args.oauth <= 0) return "oauth_0";

  if (args.oauth === 1) {
    if (args.canSubmit) return "oauth_1_ready";
    if (args.invites <= 0) return "oauth_1_invites_0";
    if (args.invites === 1) return "oauth_1_invites_1";
    return "oauth_1_invites_2";
  }

  if (args.oauth === 2) {
    if (args.canSubmit) return "oauth_2_ready";
    if (args.invites <= 0) return "oauth_2_invites_0";
    return "oauth_2_invites_1";
  }

  // oauth >= 3
  if (args.canSubmit) return "oauth_3_ready";
  return "oauth_3_invites_0";
}

/**
 * The copy row whose prose matches a gate number: 3 is the one-piece row, 2 the
 * two-piece row, 1 the three-or-more row, none the zero row. The same mapping
 * the IDV-credited count below relies on, stated from the other side.
 */
function tierRowForRequired(required: number | null): number {
  if (required === null) return 0;
  if (required >= 3) return 1;
  if (required === 2) return 2;
  return 3;
}

export function getSubmissionState(
  session: SessionStatus | null,
  invitations: VoucherInvitation[] | null | undefined,
): SubmissionState {
  const oauth = countConnectedOAuth(session);
  const invites = countSentVouchInvites(invitations, session);
  const completed = countCompletedVouches(invitations);
  const submitted = isApplicantSubmitted(session);

  // The server's numbers when it publishes them: its gate number, its gate
  // answer, its own invite count, and a copy bucket chosen from that number, so
  // the words on the screen and the gate describe the same snapshot. The
  // browser cannot count verified email addresses, which the gate counts.
  const server = serverVouchNumbers(session);
  if (server) {
    const readyForServer = submitted && server.required !== null && completed >= server.required;
    const bucket = resolveBucket({
      oauth: tierRowForRequired(server.required),
      invites: session?.vouch_summary?.sent ?? invites,
      submitted,
      readyForReview: readyForServer,
      canSubmit: server.canSubmit,
    });
    return {
      connectedOAuth: oauth,
      sentVouchInvites: invites,
      completedVouches: completed,
      requiredVouches: server.required,
      applicantSubmitted: submitted,
      canSubmit: server.canSubmit,
      readyForReview: readyForServer,
      bucket,
      copy: COPY[bucket],
    };
  }

  // ADR-012 R2 — inert unless the org asks for applicant IDV AND it passed.
  const required = requiredVouchesWithIdv(oauth, hasEarnedIdvCredit(session));

  const canSubmit =
    oauth >= 1 && required !== null && invites >= required;
  const readyForReview =
    submitted &&
    oauth >= 1 &&
    required !== null &&
    completed >= required;

  // Pick the copy bucket by the CREDITED count, not the raw one. The buckets
  // encode tier numbers in prose ("Send 2 vouch requests or connect 1 more
  // account…"), so a 2-OAuth applicant who completed IDV would otherwise be
  // told to send 2 vouches while the gate only wants 1.
  //
  // No new buckets are needed: the credit behaves exactly like one more
  // account for tier purposes, so the existing copy is already correct at the
  // credited count. 2 OAuth + IDV lands on `oauth_3_invites_0` ("Send 1 vouch
  // request to submit") — which is precisely right. `connectedOAuth` in the
  // returned state stays the REAL count, since that's what the UI counts.
  const creditedOAuth = oauth + (hasEarnedIdvCredit(session) && oauth >= 1 ? 1 : 0);
  const bucket = resolveBucket({
    oauth: creditedOAuth,
    invites,
    submitted,
    readyForReview,
    canSubmit,
  });

  return {
    connectedOAuth: oauth,
    sentVouchInvites: invites,
    completedVouches: completed,
    requiredVouches: required,
    applicantSubmitted: submitted,
    canSubmit,
    readyForReview,
    bucket,
    copy: COPY[bucket],
  };
}

// ---------- Per-vouch row copy ----------

export interface VouchRowCopy {
  title: string;
  subtitle: string;
}

/**
 * Display copy for a single vouch invitation row. Mirrors the language
 * rules in the Claude handoff doc §4 — keep applicant and operator views
 * aligned by sourcing both from this helper.
 */
export function resolveVouchRowCopy(
  invitation: Pick<VoucherInvitation, "status">,
): VouchRowCopy {
  switch (invitation.status) {
    case "complete":
    case "accepted":
      // Deliberately neutral. `"complete"` covers both a confirmed vouch and a
      // refused one (the backend collapses them so the applicant can't tell
      // which — see `VoucherInvitationStatus`), so this copy must be true under
      // either. The old "This counts toward your review requirement." would be
      // an overclaim on a declined vouch.
      return {
        title: "Vouch response received",
        subtitle: "Your voucher has completed their step.",
      };
    case "declined":
    case "expired":
    case "revoked":
      return {
        title: "Vouch not completed",
        subtitle:
          "You can send a new request or choose another verification option.",
      };
    case "pending":
    default:
      return {
        title: "Vouch request sent",
        subtitle:
          "This vouch will count once your voucher completes their step.",
      };
  }
}
